import { Order, OrderDispatchTimeStatus, OrderDispatchStatus  } from '../types';
import { APIPickTour } from '../redux/sagas/types';
import { 
  ORDER_DISPATCH_TIME_OVERDUE, 
  ORDER_DISPATCH_TIME_DUE_SOON, 
  ORDER_DISPATCH_TIME_NORMAL, 
  ORDER_DISPATCH_TIME_UNKNOWN 
} from '../constants';

/**
 * Finds the earliest dispatch time from an array of ISO timestamp strings
 * @param dispatchTimes - Array of ISO timestamp strings
 * @returns The earliest timestamp string, or null if array is empty
 */

export const findEarliestDispatchTime = (dispatchTimes: string[]): string | null => {
  if (!dispatchTimes || dispatchTimes.length === 0) {
    return null;
  }

  return dispatchTimes.reduce((earliest, current) => {
    const earliestTime = new Date(earliest).getTime();
    const currentTime = new Date(current).getTime();

    return currentTime < earliestTime ? current : earliest;
  });
};

/**
 * Calculates dispatch time status for a single dispatch time
 * @param dispatchTime - ISO timestamp string
 * @param pickTourStarted - Whether the pick tour has started
 * @returns Status object with highlight and overdue flags
 */
export const getDispatchTimeStatus = (
  dispatchTime: string | null,
): OrderDispatchTimeStatus => {
  if (!dispatchTime) {
    return { isDueSoon: false, isOverdue: false};
  }

  const now = new Date();
  const dispatch = new Date(dispatchTime);
  const diffMinutes = (dispatch.getTime() - now.getTime()) / (1000 * 60);

  // Highlight if within 15 minutes of dispatch time and not yet processed
  const isDueSoon = diffMinutes <= 15 && diffMinutes > 0;
  const isOverdue = diffMinutes <= 0;

  return {
    isDueSoon,
    isOverdue
  };
};

/**
 * Determines dispatch status for a single order
 * @param order - Order object
 * @returns Dispatch status: 'unknown', 'normal', 'due-soon', or 'overdue'
 */
export const getOrderDispatchStatus = (order: Order): OrderDispatchStatus => {
  if (!order.dispatchTime) {
    return ORDER_DISPATCH_TIME_UNKNOWN;
  }

  const status = getDispatchTimeStatus(order.dispatchTime);
  
  if (status.isOverdue) return ORDER_DISPATCH_TIME_OVERDUE;
  if (status.isDueSoon) return ORDER_DISPATCH_TIME_DUE_SOON;
  return ORDER_DISPATCH_TIME_NORMAL;
};

/**
 * Determines dispatch status for a single pick tour
 * @param pickTour - APIPickTour object
 * @returns Dispatch status: 'unknown', 'normal', 'due-soon', or 'overdue'
 */
export const getPickTourDispatchStatus = (pickTour: APIPickTour): OrderDispatchStatus => {
  if (!pickTour.metadata.ordersSummaryData || pickTour.metadata.ordersSummaryData.length === 0) {
    return ORDER_DISPATCH_TIME_UNKNOWN;
  }

  let hasDueSoon = false;
  let hasOverdue = false;

  // Check all dispatch times in all orders
  pickTour.metadata.ordersSummaryData.forEach(order => {
    if (order.dispatchTimes && order.dispatchTimes.length > 0) {
      order.dispatchTimes.forEach(dispatchTime => {
        const status = getDispatchTimeStatus(dispatchTime);
        
        if (status.isOverdue) {
          hasOverdue = true;
        } else if (status.isDueSoon) {
          hasDueSoon = true;
        }
      });
    }
  });

  // Overdue takes precedence
  if (hasOverdue) return ORDER_DISPATCH_TIME_OVERDUE;
  if (hasDueSoon) return ORDER_DISPATCH_TIME_DUE_SOON;
  return ORDER_DISPATCH_TIME_NORMAL;
};

/**
 * Determines dispatch status for a group of orders and pick tours
 * @param orders - Array of Order objects
 * @param pickTours - Array of APIPickTour objects
 * @returns Dispatch status: 'unknown', 'normal', 'due-soon', or 'overdue'
 */
export const getGroupDispatchStatus = (
  orders: Order[] = [], 
  pickTours: APIPickTour[] = []
): OrderDispatchStatus => {
  let hasDueSoon = false;
  let hasOverdue = false;

  // Check individual orders
  orders.forEach(order => {
    const status = getOrderDispatchStatus(order);
    if (status === ORDER_DISPATCH_TIME_OVERDUE) {
      hasOverdue = true;
    } else if (status === ORDER_DISPATCH_TIME_DUE_SOON) {
      hasDueSoon = true;
    }
  });

  // Check pick tours
  
  pickTours.forEach(pickTour => {
    const status = getPickTourDispatchStatus(pickTour);
    if (status === ORDER_DISPATCH_TIME_OVERDUE) {
      hasOverdue = true;
    } else if (status === ORDER_DISPATCH_TIME_DUE_SOON) {
      hasDueSoon = true;
    }
  });
  

  // Overdue takes precedence
  if (hasOverdue) return ORDER_DISPATCH_TIME_OVERDUE;
  if (hasDueSoon) return ORDER_DISPATCH_TIME_DUE_SOON;
  return ORDER_DISPATCH_TIME_NORMAL;
}
